// TelcoTrack – podpisový panel (standardní i celostránkový režim).

import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

@Component({
  selector: 'app-diary-signature-pad',
  templateUrl: './diary-signature-pad.component.html',
  styleUrls: ['./diary-signature-pad.component.scss'],
  exportAs: 'diarySigPad',
})
export class DiarySignaturePadComponent implements AfterViewInit, OnChanges {
  @ViewChild('canvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;

  /** Když true, nebere vstupy (např. během submit). */
  @Input() disabled = false;

  /** Když true, podpisová plocha se roztáhne na celou dostupnou výšku obrazovky. */
  @Input() fullPage = false;

  @HostBinding('class.diary-signature-pad--fullpage')
  get isFullPageHost(): boolean {
    return this.fullPage;
  }

  /** true = uživatel něco nakreslil od posledního clear. */
  @Output() signatureChange = new EventEmitter<boolean>();

  private ctx: CanvasRenderingContext2D | null = null;
  private drawing = false;
  private activePointerId: number | null = null;

  ngAfterViewInit(): void {
    this.initContext();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fullPage'] && !changes['fullPage'].firstChange && this.canvasRef) {
      this.initContext();
    }
  }

  private initContext(): void {
    const canvas = this.canvasRef.nativeElement;
    const targetWidth = this.fullPage ? 1200 : 560;
    const targetHeight = this.fullPage ? 600 : 180;
    if (canvas.width !== targetWidth) canvas.width = targetWidth;
    if (canvas.height !== targetHeight) canvas.height = targetHeight;
    // Jedna 2D kontextová instance + čtení pixelů v isEmpty() — willReadFrequently snižuje varování prohlížeče.
    this.ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!this.ctx) return;
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
    this.ctx.strokeStyle = '#111827';
    this.ctx.lineWidth = this.fullPage ? 3 : 2.25;
    this.fillWhite();
  }

  private fillWhite(): void {
    const canvas = this.canvasRef.nativeElement;
    const ctx = this.ctx;
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.beginPath();
  }

  onPointerDown(e: PointerEvent): void {
    if (this.disabled || !this.ctx) return;
    e.preventDefault();
    this.drawing = true;
    this.activePointerId = e.pointerId;
    this.canvasRef.nativeElement.setPointerCapture(e.pointerId);
    const { x, y } = this.eventPos(e);
    this.ctx.beginPath();
    this.ctx.moveTo(x, y);
    this.signatureChange.emit(true);
  }

  onPointerMove(e: PointerEvent): void {
    if (this.disabled || !this.drawing || !this.ctx || this.activePointerId !== e.pointerId) return;
    e.preventDefault();
    const { x, y } = this.eventPos(e);
    this.ctx.lineTo(x, y);
    this.ctx.stroke();
  }

  onPointerUp(e: PointerEvent): void {
    if (this.activePointerId !== e.pointerId) return;
    this.finishStroke(e.pointerId);
  }

  /** Uvolnit capture při opuštění canvasu během táhnutí (mobil). */
  onPointerLeave(e: PointerEvent): void {
    if (this.activePointerId === e.pointerId) {
      this.finishStroke(e.pointerId);
    }
  }

  private finishStroke(pointerId: number): void {
    this.drawing = false;
    this.activePointerId = null;
    try {
      this.canvasRef.nativeElement.releasePointerCapture(pointerId);
    } catch {
      /* capture už uvolněn */
    }
  }

  private eventPos(e: PointerEvent): { x: number; y: number } {
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }

  clear(): void {
    this.initContext();
    this.signatureChange.emit(false);
  }

  /** Obnoví plátno z uloženého PNG (GET detail po opětovném otevření). */
  loadFromDataUrl(dataUrl: string): void {
    const canvas = this.canvasRef.nativeElement;
    const ctx = this.ctx;
    if (!ctx) return;
    this.fillWhite();
    const trimmed = dataUrl.trim();
    if (!trimmed) {
      this.signatureChange.emit(false);
      return;
    }
    const img = new Image();
    img.onload = (): void => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      this.fillWhite();
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      this.signatureChange.emit(true);
    };
    img.onerror = (): void => this.signatureChange.emit(false);
    img.src = trimmed.startsWith('data:') ? trimmed : `data:image/png;base64,${trimmed}`;
  }

  /** true = žádný viditelný ink (jen bílé pozadí). */
  isEmpty(): boolean {
    const canvas = this.canvasRef.nativeElement;
    const ctx = this.ctx;
    if (!ctx) return true;
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    for (let i = 0; i < data.length; i += 4) {
      const lum = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      if (data[i + 3] > 40 && lum < 248) return false;
    }
    return true;
  }

  toPngDataUrl(): string {
    return this.canvasRef.nativeElement.toDataURL('image/png');
  }
}
